import React, {
  createContext, useContext, useEffect, useMemo, useState, ReactNode,
} from 'react';
import type { PrimaryTag } from './types';

export type Theme = 'light' | 'dark' | 'system';
/** 'auto' = use the active palette's own accent (no [data-accent] override). */
export type Accent = 'auto' | 'blue' | 'amber' | 'teal' | 'indigo' | 'purple' | 'graphite';
export type Density = 'spacious' | 'balanced' | 'compact';
export type Palette = 'sandstone' | 'caffeine' | 'elegant-luxury' | 'claude' | 'amber-minimal' | 'nature' | 'lawsofux';
export type Layout = 'list' | 'grid';
/** Folder the Search screen opens scoped to. 'all' means no default (unset). */
export type DefaultFolder = PrimaryTag | 'all';

const THEME_KEY = 'dp-theme';
const ACCENT_KEY = 'dp-accent';
const DENSITY_KEY = 'dp-density';
const PALETTE_KEY = 'dp-palette';
const LAYOUT_KEY = 'dp-layout';
const DEFAULT_FOLDER_KEY = 'dp-default-folder';

const THEMES: readonly Theme[] = ['light', 'dark', 'system'];
const ACCENTS: readonly Accent[] = ['auto', 'blue', 'amber', 'teal', 'indigo', 'purple', 'graphite'];
const DENSITIES: readonly Density[] = ['spacious', 'balanced', 'compact'];
const PALETTES: readonly Palette[] = ['sandstone', 'caffeine', 'elegant-luxury', 'claude', 'amber-minimal', 'nature', 'lawsofux'];
const LAYOUTS: readonly Layout[] = ['list', 'grid'];
const DEFAULT_FOLDERS: readonly DefaultFolder[] = ['all', 'inpatient', 'outpatient', 'icu', 'ed', 'exam', 'protocols', 'references'];

function readChoice<T extends string>(key: string, valid: readonly T[], fallback: T): T {
  try {
    const v = window.localStorage.getItem(key) as T | null;
    if (v && valid.includes(v)) return v;
  } catch (e) {
    /* ignore */
  }
  return fallback;
}

function prefersDark(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-color-scheme: dark)').matches
    : true;
}

function applyTheme(theme: Theme) {
  const dark = theme === 'dark' || (theme === 'system' && prefersDark());
  document.documentElement.classList.toggle('dark', dark);
}

type ThemeContextValue = {
  theme: Theme;
  accent: Accent;
  density: Density;
  palette: Palette;
  layout: Layout;
  defaultFolder: DefaultFolder;
  setTheme: (t: Theme) => void;
  setAccent: (a: Accent) => void;
  setDensity: (d: Density) => void;
  setPalette: (p: Palette) => void;
  setLayout: (l: Layout) => void;
  setDefaultFolder: (f: DefaultFolder) => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(() => readChoice(THEME_KEY, THEMES, 'dark'));
  const [accent, setAccent] = useState<Accent>(() => readChoice(ACCENT_KEY, ACCENTS, 'auto'));
  const [density, setDensity] = useState<Density>(() => readChoice(DENSITY_KEY, DENSITIES, 'balanced'));
  const [palette, setPalette] = useState<Palette>(() => readChoice(PALETTE_KEY, PALETTES, 'sandstone'));
  const [layout, setLayout] = useState<Layout>(() => readChoice(LAYOUT_KEY, LAYOUTS, 'grid'));
  const [defaultFolder, setDefaultFolder] = useState<DefaultFolder>(
    () => readChoice(DEFAULT_FOLDER_KEY, DEFAULT_FOLDERS, 'all')
  );

  useEffect(() => {
    applyTheme(theme);
    try { window.localStorage.setItem(THEME_KEY, theme); } catch (e) { /* ignore */ }
  }, [theme]);

  useEffect(() => {
    document.documentElement.setAttribute('data-accent', accent);
    try { window.localStorage.setItem(ACCENT_KEY, accent); } catch (e) { /* ignore */ }
  }, [accent]);

  useEffect(() => {
    document.documentElement.setAttribute('data-density', density);
    try { window.localStorage.setItem(DENSITY_KEY, density); } catch (e) { /* ignore */ }
  }, [density]);

  useEffect(() => {
    document.documentElement.setAttribute('data-palette', palette);
    try { window.localStorage.setItem(PALETTE_KEY, palette); } catch (e) { /* ignore */ }
  }, [palette]);

  useEffect(() => {
    try { window.localStorage.setItem(LAYOUT_KEY, layout); } catch (e) { /* ignore */ }
  }, [layout]);

  useEffect(() => {
    try { window.localStorage.setItem(DEFAULT_FOLDER_KEY, defaultFolder); } catch (e) { /* ignore */ }
  }, [defaultFolder]);

  // Track OS preference changes while in system mode.
  useEffect(() => {
    if (theme !== 'system' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => applyTheme('system');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [theme]);

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme, accent, density, palette, layout, defaultFolder,
      setTheme, setAccent, setDensity, setPalette, setLayout, setDefaultFolder,
    }),
    [theme, accent, density, palette, layout, defaultFolder]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
}
